import React, { useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Grid,
  Dialog,
  Typography,
  Tooltip,
  CircularProgress,
  CardActions,
  Chip,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CloseIcon from "@mui/icons-material/Close";
import { FotoBeneModel } from "../../models/FotoBeneModel";
import { deleteFotoBene, setFotoPrincipale } from "../../api/fotobene";

interface FotoGalleryProps {
  foto: FotoBeneModel[];
  onUpdate: () => void;
  loading?: boolean;
}

const FotoGalleryComponent: React.FC<FotoGalleryProps> = ({
  foto,
  onUpdate,
  loading = false,
}) => {
  console.log("FotoGalleryComponent render - foto:", foto, "length:", foto?.length, "loading:", loading);
  
  const [selectedFoto, setSelectedFoto] = useState<FotoBeneModel | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [settingPrincipaleId, setSettingPrincipaleId] = useState<number | null>(
    null
  );

  const handleDelete = async (id: number) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa foto?")) {
      return;
    }

    setDeletingId(id);
    try {
      await deleteFotoBene(id);
      onUpdate();
    } catch (error) {
      console.error("Errore eliminazione foto:", error);
      alert("Errore durante l'eliminazione della foto");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetPrincipale = async (id: number) => {
    setSettingPrincipaleId(id);
    try {
      await setFotoPrincipale(id);
      onUpdate();
    } catch (error) {
      console.error("Errore impostazione principale:", error);
      alert("Errore durante l'impostazione della foto principale");
    } finally {
      setSettingPrincipaleId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (foto.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography variant="body2" color="textSecondary">
          Nessuna foto disponibile. Carica la prima foto!
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {foto.map((f) => {
          // Estrai il nome del file dal percorso
          const fileName = f.percorso_file.split('/').pop() || 'Foto';
          
          return (
            <Paper 
              key={f.id} 
              variant="outlined" 
              sx={{ p: 1, position: "relative", width: 150 }}
            >
              <Box 
                sx={{
                  width: "100%",
                  height: 120,
                  bgcolor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {/* Icona X per eliminare */}
                <IconButton
                  sx={{ position: "absolute", top: 4, right: 4, bgcolor: "white", zIndex: 2 }}
                  onClick={() => handleDelete(f.id)}
                  disabled={deletingId === f.id}
                  size="small"
                >
                  {deletingId === f.id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CloseIcon fontSize="small" />
                  )}
                </IconButton>

                {/* Badge principale in alto a sinistra */}
                {f.is_principale && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      zIndex: 2,
                      bgcolor: "primary.main",
                      color: "white",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StarIcon sx={{ fontSize: 16 }} />
                  </Box>
                )}

                {/* Icona stella per impostare principale */}
                {!f.is_principale && (
                  <Tooltip title="Imposta come principale">
                    <span>
                      <IconButton
                        sx={{ 
                          position: "absolute", 
                          bottom: 4, 
                          right: 4, 
                          bgcolor: "white",
                          zIndex: 2,
                          p: 0.5,
                        }}
                        onClick={() => handleSetPrincipale(f.id)}
                        disabled={settingPrincipaleId === f.id}
                        size="small"
                      >
                        {settingPrincipaleId === f.id ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <StarBorderIcon sx={{ fontSize: 16, color: "action.disabled" }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {/* Immagine con click per visualizzare */}
                <img
                  src={f.thumbnail_url}
                  alt={fileName}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedFoto(f)}
                />
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Dialog per visualizzazione full-size */}
      <Dialog
        open={selectedFoto !== null}
        onClose={() => setSelectedFoto(null)}
        maxWidth="lg"
        fullWidth
      >
        {selectedFoto && (
          <Box sx={{ position: "relative", bgcolor: "black" }}>
            <IconButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "white",
                bgcolor: "rgba(0,0,0,0.5)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
              onClick={() => setSelectedFoto(null)}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={selectedFoto.url}
              alt={`Foto ${selectedFoto.id}`}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
            <Box
              sx={{
                p: 2,
                bgcolor: "rgba(0,0,0,0.8)",
                color: "white",
                textAlign: "center",
              }}
            >
              <Typography variant="caption">
                {selectedFoto.is_principale && (
                  <StarIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 1 }} />
                )}
                Foto #{selectedFoto.ordine}
              </Typography>
            </Box>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default FotoGalleryComponent;
